import React, { useEffect, useState, useRef } from "react";

import { CloseRedIcon, CloseGreyIcon } from "../../../assets/icons";
import { Switch } from "@mui/material";
import Loader from "../../shared/Loader";
import { useParams } from "react-router-dom";
import { subcategoryService } from "../../../services/subcategoriesService";
import { SubcategorySelectDTO, SubcategorySelectWithCategoryDTO } from "../../../models/subcategories/subcategories.response";
import { useNotification } from "../../toster-notification/NotificationContext";
import { ProductTagType } from "../../../models/base/ProductTagType";
import { productService } from "../../../services/productService";
import { ProductVariantDTO } from "../../../models/product/product.request";
import { validateMenuItemName, validateMenuItemProductVariants } from "../../../utils/validators";

interface AddMenuItemViewProps {
    onClose: () => void;
}

const AddMenuItemView: React.FC<AddMenuItemViewProps> = ({ onClose }) => {

    const [name, setName] = useState<string>("");
    const [nameError, setNameError] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [additionalDescription, setAdditionalDescription] = useState<string>("");
    const [productVariants, setProductVariants] = useState<ProductVariantDTO[]>([{
        displayOrder: 0,
        label: "kom",
        price: 0,
    }]);
    const [priceError, setPriceError] = useState<string>("");
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [subcategoryIds, setSubcategoryIds] = useState<string[]>([]);
    const [selectedTags, setSelectedTags] = useState<ProductTagType[]>([]);
    const [isNew, setIsNew] = useState<boolean>(false);
    const [isActive, setIsActive] = useState<boolean>(true);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const tags: string[] = Object.keys(ProductTagType).filter(key => isNaN(Number(key)));

    const [subcategories, setSubcategories] = useState<SubcategorySelectWithCategoryDTO[]>([]);

    const [loading, setLoading] = useState<boolean>(false);

    const { localId } = useParams<{ localId: string }>();
    const { notify } = useNotification();

    useEffect(() => {
        fetchSubcategories();
    }, []);

    const fetchSubcategories = async () => {
        if (!localId)
            return;

        setLoading(true);

        try {
            const response = await subcategoryService.getSubcategoriesSelectWithCategory(localId);

            if (response.success && response.data) {
                setSubcategories(response.data);
            }
        } catch (error) {
            notify("error", "Došlo je do greške prilikom učitavanja podkategorija.");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateMenuItem = async () => {
        if (!localId) {
            notify("error", "ID lokala nije pronađen.");
            return;
        }

        setNameError("");
        setPriceError("");

        let isValid = true;

        let nameError = validateMenuItemName(name);
        if(nameError != null){
            setNameError(nameError);
            isValid = false;
        }

        let productVariantsError = validateMenuItemProductVariants(productVariants);
        if(productVariantsError !== null){
            setPriceError(productVariantsError)
            isValid = false;
        }


        if (!isValid) {
            return;
        }

        setLoading(true);
        try {

            const formData = new FormData();

            formData.append("name", name || "");
            formData.append("description", description || "");
            formData.append("description2", additionalDescription || "");
            formData.append("isNew", String(isNew));
            formData.append("isActive", String(isActive));
            
            productVariants.forEach((variant, index) => {
                if(variant.label) {
                    formData.append(`productVariants[${index}].label`, variant.label);
                }
                formData.append(`productVariants[${index}].price`, variant.price.toString());
                formData.append(`productVariants[${index}].displayOrder`, variant.displayOrder.toString());
                if (variant.oldPrice !== undefined && variant.oldPrice !== null) {
                    formData.append(`productVariants[${index}].oldPrice`, variant.oldPrice.toString());
                }
            });

            subcategoryIds.forEach(id =>
                formData.append("subcategoriesIds", id)
            );

            selectedTags.forEach(tag =>
                formData.append("tags", tag.toString())
            );

            if (imageFile) {
                formData.append("imageFile", imageFile);
            }

            const response = await productService.createProduct(localId, formData);

            if (response.success) {
                notify("success", "Stavka menija je uspešno kreirana.");
                onClose();
            } else {
                notify("error", response.message || "Došlo je do greške prilikom kreiranja stavke menija.");
            }

        } catch (error: any) {
            notify("error", error.message || "Došlo je do greške prilikom kreiranja stavke menija.");
        } finally {
            setLoading(false);
        }
    };

    const handleAddVariant = () => {

        let maxDisplayOrder = 0;
        productVariants.forEach(variant => {
            if (variant.displayOrder > maxDisplayOrder) {
                maxDisplayOrder = variant.displayOrder;
            }
        });

        const newVariant: ProductVariantDTO = {
            label: "",
            price: 0,
            displayOrder: maxDisplayOrder + 1,
        };
        setProductVariants([...productVariants, newVariant]);
    };

    if (!localId) {
        return <div>Nije pronađen lokal</div>;
    }

    if (loading) {
        return <Loader />;
    }

    return (
        <section className="modal-section product">
            <div className="w-layout-blockcontainer container w-container">
                <div className="modal-wrapper">
                    <div className="modal-div product">
                        <div className="modal-header-div">
                            <div>
                                <div className="modal-header">Nova stavka menija</div>
                                <div className="description-text">
                                    Unesite podatke o proizvodu<br />
                                </div>
                            </div>
                            <div className="modal-button-div" onClick={onClose}>
                                <img loading="lazy" src={CloseGreyIcon} alt="" className="icon-size-20 close" />
                            </div>
                        </div>
                        <div className="modal-form-block w-form">
                            <form className="modal-form">

                                <div className="modal-form-div">
                                    <label className="modal-form-label">Naziv *</label>
                                    <input className="form-input grey w-input" placeholder="npr. Margarita, Carbonara" type="text" name="productName"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                    />
                                    <div className="error-text">{nameError}</div>
                                </div>

                                <div className="modal-form-div">
                                    <label className="modal-form-label">Opis</label>
                                    <textarea placeholder="Opis proizvoda (npr. sastojci, način pripreme)" className="form-textarea grey w-input" name="productDescription"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                    ></textarea>
                                </div>

                                <div className="modal-form-div">
                                    <label className="modal-form-label">Dodatan opis</label> 
                                    <textarea placeholder="Dodatni opis proizvoda (npr. alergeni, napomene)" className="form-textarea grey w-input" name="productDescription2"
                                        value={additionalDescription}
                                        onChange={(e) => setAdditionalDescription(e.target.value)}
                                    ></textarea>
                                </div>

                                <div className="modal-form-div">
                                    <label className="modal-form-label">Upravljanje varijantama</label>
                                    {productVariants.map((variant) => (
                                        <div className="div-block-104" key={variant.displayOrder}>

                                            <div className="div-block-107">
                                                <label className="modal-form-label small">Obrisi</label>
                                                <div id="w-node-e30dd073-8f28-8fc4-9ee3-594026a653e9-e3d18463" className="button-primary red-bg remove"
                                                    onClick={() => setProductVariants(productVariants.filter((v) => v.displayOrder !== variant.displayOrder))}>
                                                    <img src="https://cdn.prod.website-files.com/6919e066813f7ba31cee91ac/692302d998712eeacd56aeec_close%20(4).png" loading="lazy" alt="" className="icon-size-16" />
                                                </div>
                                            </div>

                                            <div className="div-block-107">
                                                <label className="modal-form-label small">Naziv varijante*</label>
                                                <input className="form-input grey w-input" placeholder="npr. &quot;kom&quot;, &quot;šolja&quot;, za picu &quot;Mala&quot;, &quot;Srednja&quot;, &quot;Velika&quot;" type="text" name="variantName"
                                                    value={variant.label}
                                                    onChange={(e) => {
                                                        const newLabel = e.target.value;
                                                        setProductVariants(productVariants.map((v) => {
                                                            if (v.displayOrder === variant.displayOrder) {
                                                                return { ...v, label: newLabel };
                                                            }
                                                            return v;
                                                        }));
                                                    }}
                                                />
                                            </div>

                                            <div className="div-block-107">
                                                <label className="modal-form-label small">Cena (RSD) *</label>
                                                <input className="form-input grey w-input" placeholder="Cena" type="number"
                                                    value={variant.price}
                                                    onChange={(e) => {
                                                        if (Number(e.target.value) < 1)
                                                            return;

                                                        const newPrice = Number(e.target.value);
                                                        setProductVariants(productVariants.map((v) => {
                                                            if (v.displayOrder === variant.displayOrder) {
                                                                return { ...v, price: newPrice };
                                                            }
                                                            return v;
                                                        }));
                                                    }}
                                                />
                                            </div>

                                            <div className="div-block-107">
                                                <label className="modal-form-label small">Stara cena (RSD)</label>
                                                {variant.oldPrice ? (
                                                    <input className="form-input grey w-input" placeholder="Cena" type="number"
                                                        value={variant.oldPrice}
                                                        onChange={(e) => {
                                                            if (Number(e.target.value) < 1)
                                                                return;

                                                            const newPrice = Number(e.target.value);
                                                            setProductVariants(productVariants.map((v) => {
                                                                if (v.displayOrder === variant.displayOrder) {
                                                                    return { ...v, oldPrice: newPrice };
                                                                }
                                                                return v;
                                                            }));
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="button-primary green-bg" onClick={() => {
                                                        setProductVariants(productVariants.map((v) => {
                                                            if (v.displayOrder === variant.displayOrder) {
                                                                return { ...v, oldPrice: v.price ? v.price : 1 };
                                                            }
                                                            return v;
                                                        }));
                                                    }}>
                                                        <div>Stavi na akciju</div>
                                                    </div>
                                                )}

                                                {variant.oldPrice && (
                                                    <div style={{ "gridColumn": "4" }} className="text-block-54" onClick={() => {
                                                        setProductVariants(productVariants.map((v) => {
                                                            if (v.displayOrder === variant.displayOrder) {
                                                                return { ...v, oldPrice: undefined }
                                                            }
                                                            return v;
                                                        }))
                                                    }}>Ukloni akciju</div>
                                                )}
                                            </div>

                                        </div>
                                    ))}
                                    <div className="button-primary align-right" onClick={handleAddVariant}>
                                        <div>Dodaj varijantu</div>
                                    </div>
                                    <div className="error-text">{priceError}</div>
                                </div>

                                <div className="modal-form-div">
                                    <label className="modal-form-label">Upload slike</label>
                                    <input ref={fileInputRef} className="" style={{ alignContent: "" }} type="file"
                                        onChange={(e) => {
                                            if (e.target.files && e.target.files.length > 0) {
                                                setImageFile(e.target.files[0]);
                                            }
                                        }}
                                    />
                                    <div className="modal-image-div">
                                        <img src={imageFile ? URL.createObjectURL(imageFile) : ""} loading="lazy" className="modal-image" />
                                        <div className="modal-image-remove-div"
                                            onClick={() => {
                                                setImageFile(null);
                                                if (fileInputRef.current) {
                                                    fileInputRef.current.value = "";
                                                }
                                            }}
                                        >
                                            <img loading="lazy" src={CloseRedIcon} alt="" className="icon-size-16 pointer" />
                                        </div>
                                    </div>
                                </div>

                                <div className="modal-form-div">
                                    <label className="modal-form-label">Podkategorija</label>
                                    <div className="div-block-14">

                                        {subcategories.length > 0 ? (
                                            subcategories.map((subcategory) => (
                                                <div key={subcategory.id} className={subcategoryIds.includes(subcategory.id) ? "small-box-div purple-bg pointer" : "small-box-div pointer"}
                                                    onClick={() => {
                                                        if (subcategoryIds.includes(subcategory.id)) {
                                                            setSubcategoryIds(subcategoryIds.filter(id => id !== subcategory.id));
                                                        } else {
                                                            setSubcategoryIds([...subcategoryIds, subcategory.id]);
                                                        }
                                                    }}
                                                >
                                                    <div>{subcategory.name}</div>
                                                    <div className="text-block-11">({subcategory.categoryName})</div>
                                                </div>
                                            ))
                                        ) : (
                                            <div>Nema dostupnih podkategorija</div>
                                        )}

                                    </div>
                                </div>

                                <div className="modal-form-div">
                                    <label className="modal-form-label">Tagovi</label>
                                    <div className="div-block-14">
                                        {tags.map(tag => {

                                            const tagValue = ProductTagType[tag as keyof typeof ProductTagType];
                                            const isSelected = selectedTags.includes(tagValue);

                                            return (

                                                <div
                                                    key={tag}
                                                    className={isSelected ? "small-box-div purple-bg pointer" : "small-box-div pointer"}
                                                    onClick={() => {
                                                        if (selectedTags.includes(tagValue)) {
                                                            setSelectedTags(selectedTags.filter(name => name !== tagValue));
                                                        } else {
                                                            setSelectedTags([...selectedTags, tagValue]);
                                                        }
                                                    }}
                                                >
                                                    <div>{tag}</div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>

                                <div className="modal-form-div f-horizontal">
                                    <label className="modal-form-label">Oznaci kao NOVO</label>
                                    <Switch
                                        checked={isNew}
                                        onChange={(e) => setIsNew(e.target.checked)}
                                    />
                                </div>

                                <div className="modal-form-div f-horizontal">
                                    <label className="modal-form-label">Akivan proizvod *</label>
                                    <Switch
                                        checked={isActive}
                                        onChange={(e) => setIsActive(e.target.checked)}
                                    />
                                </div>

                            </form>
                        </div>
                        <div className="modal-buttons">
                            <div className="button-secondary medium" onClick={onClose}>
                                <div>Otkaži</div>
                            </div>
                            <div className="button-primary medium" onClick={() => handleCreateMenuItem()}>
                                <div>Kreiraj</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default AddMenuItemView;